'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight, Mail, CheckCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

type ForgotPasswordFormData = {
  email: string;
};

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({ defaultValues: { email: '' } });

  const sendResetLink = async (email: string) => {
    const supabase = createClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      await sendResetLink(data.email);
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch {
      // Per spec: always show success message regardless of whether email is registered
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendLoading(true);
    try {
      await sendResetLink(submittedEmail || getValues('email'));
    } catch {
      // Non-blocking
    } finally {
      setResendLoading(false);
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary flex-col justify-between p-12 text-white">
        <Link href="/" className="flex items-center gap-3">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl tracking-tight">Eventz</span>
        </Link>
        <div>
          <h1 className="text-4xl font-extrabold leading-tight mb-4">
            Forgot your<br />password?
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            No worries — it happens to the best of us. Enter your email and we&apos;ll send you a secure reset link.
          </p>
        </div>
        <p className="text-white/50 text-xs">© 2026 Eventz, Inc.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-extrabold text-xl text-foreground">Eventz</span>
          </Link>

          {!submitted ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-extrabold text-foreground mb-2">Reset your password</h2>
                <p className="text-muted-foreground text-sm">
                  Enter the email address associated with your account and we&apos;ll send you a password reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
                  <div className="relative">
                    <input
                      type="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address',
                        },
                      })}
                      className="w-full px-4 py-3 pl-11 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                      placeholder="you@example.com"
                    />
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isLoading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Send Reset Link <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/sign-up-login-screen"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back to Sign In
                </Link>
              </div>
            </>
          ) : (
            /* Success state */
            <div className="text-center fade-in">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-success" />
              </div>
              <h2 className="text-2xl font-extrabold text-foreground mb-3">Check your inbox</h2>
              <p className="text-muted-foreground text-sm mb-2">
                If the email is registered, a password reset link has been sent.
              </p>
              <p className="text-muted-foreground text-sm mb-8">
                We sent it to{' '}
                <span className="font-semibold text-foreground">{submittedEmail}</span>
              </p>

              <div className="p-4 bg-muted rounded-2xl border border-border mb-6 text-left">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Didn&apos;t receive the email?</p>
                <p className="text-xs text-muted-foreground">Check your spam folder, or resend the link below.</p>
              </div>

              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading || resendCooldown > 0}
                className="w-full py-3 border border-border bg-card text-foreground font-semibold rounded-xl hover:bg-muted transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 mb-4"
              >
                {resendLoading ? (
                  <span className="w-5 h-5 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  'Resend Reset Link'
                )}
              </button>

              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
